<template>
  <div class="profile" data-testid="profile">
    <Heading class="profile__heading" :level="2">
      Qual perfil de investidor deseja aplicar na carteira?
    </Heading>
    <LoaderIndicator v-if="isLoading" with-container />
    <SelectionBox
      v-else-if="profiles"
      class="profile__selection-box"
      aria-label="Qual perfil de investidor deseja aplicar na carteira?"
      type="single"
      :value="selectedOption"
      :options="options"
      @change="handleChangeOption"
    />
    <ButtonContainer class="profile__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          size="large"
          :disabled="isDisabled"
          @click="onClick"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  Heading,
  LoaderIndicator,
  SelectionBox,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';

import {
  getInvestmentProfiles,
  IInvestmentProfile
} from '@/modules/catalog/services/funds';

const router = useRouter();

const updateData = inject('updateData', (key: string, data: object) => ({
  key,
  data
}));

const isLoading = ref(true);
const profiles = ref<IInvestmentProfile[] | null>(null);
const selectedOption = ref('');

const options = computed(() => {
  return profiles.value?.map(item => {
    return {
      title: item.formatted,
      value: item.value
    };
  });
});

const selectedOptionFormatted = computed(() => {
  const data = profiles.value?.filter(
    item => item.value === selectedOption.value
  );

  return data![0];
});

const isDisabled = computed(() => {
  return !selectedOption.value;
});

function handleChangeOption(value: string) {
  selectedOption.value = value;
}

function onClick() {
  updateData('profile', selectedOptionFormatted.value);
}

async function fetchInvestmentProfiles() {
  try {
    isLoading.value = true;

    profiles.value = await getInvestmentProfiles();
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível carregar as informações. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });

    router.push({ name: 'catalog' });
  } finally {
    isLoading.value = false;
  }
}

onMounted(async () => {
  await fetchInvestmentProfiles();
});
</script>

<style lang="less" scoped>
.profile {
  max-width: 832px;
  margin: 0 auto;
  padding: 0 @size-spacing-x400 @size-spacing-x1800;

  &__heading {
    margin-top: @size-spacing-x1100;
  }

  &__selection-box {
    margin-top: @size-spacing-x1000;
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
